import {
  AvivaLogoImage as avivaLogo,
  MarkelLogoImage as markelLogo,
  MediaIconUrl as ShowreelIcon,
  PlaneIconUrl as PlaneIcon,
  ShieldIconUrl as ShieldIcon,
  HmrcLogoIconUrl as HmrcIcon,
  ProfiledocIconUrl as ProfileDocIcon,
  LinkIconUrl as LinkIcon,
} from "assets";

import {
  AddOnCodes,
  addOnDescriptionLinkMap,
  addOnDescriptionMap,
  addOnNamesMap,
} from "../../../constants";
import { PlanCodePeriod } from "../../../types";

export interface IAddOnFeature {
  title: string;
  description?: string;
  icon: any;
  iconAlt?: string;
  additionalIcon?: string;
  additionalIconAlt?: string;
  link?: string;
}

export const getModalTexts = (isCheckoutSpeedbump?: boolean) => ({
  modalTitle: isCheckoutSpeedbump
    ? "common:performer.billing.ecpModalSpeedbump.title"
    : "common:billing.ecpModal.title",
  pricePrefix: isCheckoutSpeedbump ? "" : "billing.ecp.pricePrefix",
  primaryCta: isCheckoutSpeedbump
    ? "common:performer.billing.ecpModalSpeedbump.primaryCTA"
    : "common:button.label.continue",
  secondaryCta: isCheckoutSpeedbump
    ? "common:performer.billing.ecpModalSpeedbump.secondaryCTA"
    : "common:button.label.close",
  closePopup: "common:button.label.cancel",
  disclaimer: "common:billing.planSelector.addOns.ecp.description",
  disclaimerLink: "common:billing.planSelector.addOns.ecp.descriptionLink",
});

export const getPeriodText = (code: PlanCodePeriod) => {
  return code === PlanCodePeriod.Annual
    ? "common:billing.planSelector.addOns.price.annual"
    : "common:billing.planSelector.addOns.price.monthly";
};

export const getAddOnTexts = (addOnCode: AddOnCodes) => ({
  title: addOnNamesMap[addOnCode],
  description: addOnDescriptionMap[addOnCode].planSelector,
  descriptionLink: addOnDescriptionLinkMap[addOnCode],
  vatPrice: "common:billing.vat",
  button: {
    selected: "common:button.label.remove",
    unselected: "common:button.label.add",
  },
});

export const getAddOnTermsAndConditions = (addOnCode: AddOnCodes) => {
  if (
    addOnCode === AddOnCodes.FilmMakersMonthly ||
    addOnCode === AddOnCodes.FilmMakersAnnual
  ) {
    return {
      termsAndConditions:
        "common:billing.planSelector.addOns.fm.termsAndConditions",
      termsAndConditionsLink:
        "https://www.spotlight.com/members/expand-your-horizons-with-filmmakers-premium/",
    };
  }

  return null;
};

const ecpAddOnMainFeatures: IAddOnFeature[] = [
  {
    title:
      "common:performer.planSelector.addOns.ecp.features.accidentInsurance.title",
    description:
      "common:performer.planSelector.addOns.ecp.features.accidentInsurance.description",
    icon: ShieldIcon,
    iconAlt:
      "common:performer.planSelector.addOns.ecp.features.accidentInsurance.title",
    additionalIcon: avivaLogo,
    additionalIconAlt:
      "common:performer.planSelector.addOns.ecp.features.aviva",
  },
  {
    title:
      "common:performer.planSelector.addOns.ecp.features.taxInsurance.title",
    description:
      "common:performer.planSelector.addOns.ecp.features.taxInsurance.description",
    icon: HmrcIcon,
    iconAlt:
      "common:performer.planSelector.addOns.ecp.features.taxInsurance.title",
    additionalIcon: markelLogo,
    additionalIconAlt:
      "common:performer.planSelector.addOns.ecp.features.aviva",
  },
  {
    title:
      "common:performer.planSelector.addOns.ecp.features.travelInsurance.title",
    description:
      "common:performer.planSelector.addOns.ecp.features.travelInsurance.description",
    icon: PlaneIcon,
    iconAlt:
      "common:performer.planSelector.addOns.ecp.features.travelInsurance.title",
    additionalIcon: avivaLogo,
    additionalIconAlt:
      "common:performer.planSelector.addOns.ecp.features.aviva",
  },
];

const addOnSubFeatures = [
  {
    title:
      "common:performer.planSelector.addOns.ecp.features.subFeatureAdditional",
    features: [
      {
        title: "common:performer.planSelector.addOns.ecp.features.media.title",
        description:
          "common:performer.planSelector.addOns.ecp.features.media.description",
        icon: ShowreelIcon,
        iconAlt:
          "common:performer.planSelector.addOns.ecp.features.media.title",
      },
    ],
  },
];

const filmMakersMainFeatures = [
  {
    title: "common:performer.planSelector.addOns.fm.features.profile.title",
    description:
      "common:performer.planSelector.addOns.fm.features.profile.description",
    icon: ProfileDocIcon,
    iconAlt: "common:performer.planSelector.addOns.fm.features.profile.title",
  },
  {
    title: "common:performer.planSelector.addOns.fm.features.profileLink.title",
    description:
      "common:performer.planSelector.addOns.fm.features.profileLink.description",
    icon: LinkIcon,
    iconAlt:
      "common:performer.planSelector.addOns.fm.features.profileLink.title",
  },
];

interface IFeaturesType {
  mainFeatures: IAddOnFeature[];
  subFeatures: {
    title: string;
    features: IAddOnFeature[];
  }[];
}

export const ecpAddOnFeatures: IFeaturesType = {
  mainFeatures: ecpAddOnMainFeatures,
  subFeatures: addOnSubFeatures,
};

export const filmMakersAddOnsFeatures: IFeaturesType = {
  mainFeatures: filmMakersMainFeatures,
  subFeatures: [],
};

export const features = {
  [AddOnCodes.AnnualBundle]: ecpAddOnFeatures,
  [AddOnCodes.MonthlyBundle]: ecpAddOnFeatures,
  [AddOnCodes.FilmMakersAnnual]: filmMakersAddOnsFeatures,
  [AddOnCodes.FilmMakersMonthly]: filmMakersAddOnsFeatures,
};

export const BASE_ANNUAL_TEXTS = {
  title: "common:billing.addOn.planSelector.annual.title",
  period: "common:label.year",
  badgeText: "common:billing.label.deal",
  button: {
    selected: "common:button.label.selected",
    unselected: "common:button.label.choose",
  },
};

export const BASE_MONTHLY_TEXTS = {
  title: "common:billing.addOn.planSelector.monthly.title",
  period: "common:label.month",
  button: {
    selected: "common:button.label.selected",
    unselected: "common:button.label.choose",
  },
};

export const injectPlanButtonLabel = (texts: Record<string, any>) => {
  return {
    ...texts,
    button: {
      ...texts.button,
      selected: "common:button.label.existingSubscription",
    },
  };
};
